import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Empty, Tooltip, Popover, Input, Checkbox, Button } from 'antd';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import * as XLSX from 'xlsx';
import Spinner from '../../CustomComponents/Spinner';
import { createBillerColumns } from './billerColumnConfig';
import { getAriaSortValue } from '../JobList/columnHelpers';
import * as Styled from '../JobList/TanStackTableStyles';

const ITEMS_PER_PAGE = 50;

const FilterPopoverContent = ({ column, facetedRows }) => {
  const [search, setSearch] = useState('');
  const currentFilter = column.getFilterValue() ?? [];

  const getUniqueValues = column.columnDef.meta?.getUniqueValues;
  const isDateColumn = column.columnDef.meta?.isDateColumn;

  // Chronological sort for date columns; 'N/A' / unparseable values go last.
  const sortLabels = (a, b) => {
    if (isDateColumn) {
      const ta = new Date(a).getTime();
      const tb = new Date(b).getTime();
      const aValid = !Number.isNaN(ta);
      const bValid = !Number.isNaN(tb);
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      return tb - ta;
    }
    return a.localeCompare(b);
  };

  const options = getUniqueValues
    ? getUniqueValues(facetedRows || [])
        .filter(label => label.toLowerCase().includes(search.toLowerCase()))
        .sort(sortLabels)
        .map(label => ({ label }))
    : Array.from(column.getFacetedUniqueValues().entries())
        .map(([value]) => ({
          label: value != null ? String(value) : 'N/A'
        }))
        .filter(({ label }) =>
          label.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => sortLabels(a.label, b.label));

  return (
    <div style={{ minWidth: 180, maxWidth: 260, padding: '0 8px' }}>
      <Input
        size='small'
        placeholder='Search...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {options.length === 0 ? (
          <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, padding: '4px 0' }}>
            No options
          </div>
        ) : (
          options.map(({ label }) => (
            <div key={label} style={{ padding: '3px 0' }}>
              <Checkbox
                checked={currentFilter.includes(label)}
                onChange={e => {
                  const newValues = e.target.checked
                    ? [...currentFilter, label]
                    : currentFilter.filter(v => v !== label);
                  column.setFilterValue(newValues.length > 0 ? newValues : undefined);
                }}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
              </Checkbox>
            </div>
          ))
        )}
      </div>
      {currentFilter.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f2f5', marginTop: 8, paddingTop: 6, textAlign: 'right' }}>
          <Button
            type='link'
            size='small'
            onClick={() => column.setFilterValue(undefined)}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

const BillerTanStackTable = ({
  data,
  loading,
  searchValue,
  viewCompleteBillerJob,
  onExportReady,
  visibleColumns,
  setVisibleColumns,
  clearFiltersTrigger
}) => {
  const [theme] = useSelector(state => [state.themeReducer]);

  const [sortingState, setSortingState] = useState([
    { id: 'submissionTime', desc: true }
  ]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [filterVisible, setFilterVisible] = useState({});

  const FILTER_STORAGE_KEY = 'biller_column_filters';

  // Load persisted column filters from session storage on mount
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      setColumnFilters(saved ? JSON.parse(saved) : []);
    } catch {
      setColumnFilters([]);
    }
  }, []);

  // Persist column filters to session storage whenever they change
  useEffect(() => {
    if (columnFilters.length > 0) {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
    } else {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    }
  }, [columnFilters]);

  useEffect(() => {
    setGlobalFilter(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    if (clearFiltersTrigger) {
      setColumnFilters([]);
    }
  }, [clearFiltersTrigger]);

  useEffect(() => {
    setColumnVisibility(visibleColumns);
  }, [visibleColumns]);

  const handleSortingChange = updater => {
    const newSorting =
      typeof updater === 'function' ? updater(sortingState) : updater;
    setSortingState(newSorting);
  };

  const handleColumnVisibilityChange = updater => {
    const newVisibility =
      typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newVisibility);
    setVisibleColumns(newVisibility);
  };

  const columns = useMemo(
    () => createBillerColumns({ viewCompleteBillerJob }),
    [viewCompleteBillerJob]
  );

  const tableData = useMemo(() => data || [], [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: ITEMS_PER_PAGE,
        pageIndex: 0
      }
    },
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
    enableMultiSort: true,
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      sorting: sortingState,
      globalFilter,
      columnVisibility,
      columnFilters
    },
    onSortingChange: handleSortingChange,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: row => row.jobId
  });

  const exportToExcel = useCallback(() => {
    const visibleRows = table.getRowModel().rows;
    const visibleColumnIds = table
      .getVisibleLeafColumns()
      .filter(col => col.id !== 'actions')
      .map(col => col.id);

    const exportData = visibleRows.map(row => {
      const rowData = {};
      visibleColumnIds.forEach(colId => {
        const cell = row.getAllCells().find(c => c.column.id === colId);
        const column = table.getColumn(colId);
        if (cell && column) {
          rowData[column.columnDef.header] = cell.getValue();
        }
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Biller Jobs');

    const fileName = `biller_jobs_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [table]);

  useEffect(() => {
    if (onExportReady) {
      onExportReady(exportToExcel);
    }
  }, [exportToExcel, onExportReady]);

  const isEmpty = !data || data.length === 0;

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const container = tableContainerRef.current;
    const tableEl = tableRef.current;
    if (!container) return;
    let rafId;
    const updateWidth = () => {
      rafId = requestAnimationFrame(() => {
        if (topScrollRef.current?.firstChild) {
          topScrollRef.current.firstChild.style.width = `${container.scrollWidth}px`;
        }
      });
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    if (tableEl) observer.observe(tableEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [data, columnVisibility]);

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Styled.TableContainer
        ref={tableContainerRef}
        onScroll={handleBottomScroll}
        style={{ position: 'relative' }}
      >
        {/* Top scrollbar mirror */}
        <Styled.TopScrollbar ref={topScrollRef} onScroll={handleTopScroll}>
          <div style={{ height: 1 }} />
        </Styled.TopScrollbar>

        {loading && (
          <Styled.LoadingOverlay>
            <Spinner />
          </Styled.LoadingOverlay>
        )}

        {isEmpty && !loading ? (
          <Styled.EmptyState>
            <Empty description='No jobs found' />
          </Styled.EmptyState>
        ) : (
          <>
            <Styled.StyledTable
              ref={tableRef}
              role='table'
              aria-label='Biller job list table'
              style={{
                width: table.getTotalSize(),
                minWidth: '100%',
                tableLayout: 'fixed',
                transition: table.getState().columnSizingInfo.isResizingColumn
                  ? 'none'
                  : 'width 0.15s ease'
              }}
            >
              <Styled.StyledTableHead role='rowgroup'>
                {table.getHeaderGroups().map(headerGroup => (
                  <Styled.StyledTableHeaderRow key={headerGroup.id} role='row'>
                    {headerGroup.headers.map(header => (
                      <Styled.StyledTableHeader
                        key={header.id}
                        role='columnheader'
                        aria-sort={getAriaSortValue(
                          header.column.getIsSorted()
                        )}
                        style={{ width: header.getSize() }}
                        onClick={
                          header.column.getCanSort()
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                        sortable={header.column.getCanSort()}
                        sorted={header.column.getIsSorted()}
                        tabIndex={header.column.getCanSort() ? 0 : -1}
                        onKeyDown={e => {
                          if (
                            header.column.getCanSort() &&
                            (e.key === 'Enter' || e.key === ' ')
                          ) {
                            header.column.getToggleSortingHandler()(e);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {header.column.getCanSort() && (
                            <Styled.SortIndicator
                              direction={header.column.getIsSorted()}
                            >
                              <span className='sort-arrow-up'>▲</span>
                              <span className='sort-arrow-down'>▼</span>
                            </Styled.SortIndicator>
                          )}

                          <span style={{ flex: 1 }}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>

                          {header.column.getCanFilter() && (
                            <span onClick={e => e.stopPropagation()}>
                              <Popover
                                content={
                                  <FilterPopoverContent
                                    column={header.column}
                                    facetedRows={header.column.getFacetedRowModel().rows}
                                  />
                                }
                                trigger='click'
                                visible={filterVisible[header.id] || false}
                                onVisibleChange={visible =>
                                  setFilterVisible(prev => ({
                                    ...prev,
                                    [header.id]: visible
                                  }))
                                }
                                placement='bottomRight'
                              >
                                <FilterOutlined
                                  style={{
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    color: header.column.getFilterValue()
                                      ? '#47a4ea'
                                      : 'rgba(0,0,0,0.25)'
                                  }}
                                />
                              </Popover>
                            </span>
                          )}
                        </div>

                        {header.column.getCanResize() && (
                          <Styled.ResizeHandle
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            isResizing={header.column.getIsResizing()}
                            onClick={e => e.stopPropagation()}
                          />
                        )}
                      </Styled.StyledTableHeader>
                    ))}
                  </Styled.StyledTableHeaderRow>
                ))}
              </Styled.StyledTableHead>

              <Styled.StyledTableBody role='rowgroup'>
                {table.getRowModel().rows.map(row => (
                  <Styled.StyledTableRow key={row.id} role='row'>
                    {row.getVisibleCells().map(cell => (
                      <Styled.StyledTableCell key={cell.id} role='cell'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Styled.StyledTableCell>
                    ))}
                  </Styled.StyledTableRow>
                ))}
              </Styled.StyledTableBody>
            </Styled.StyledTable>

            <Styled.PaginationContainer>
              <Styled.PaginationInfo>
                Showing{' '}
                {table.getState().pagination.pageIndex * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * ITEMS_PER_PAGE,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                of {table.getFilteredRowModel().rows.length} results
                {(globalFilter || columnFilters.length > 0) &&
                  tableData.length !==
                    table.getFilteredRowModel().rows.length &&
                  ` (filtered from ${tableData.length} total)`}
              </Styled.PaginationInfo>

              <Styled.PaginationControls>
                <Tooltip title='First Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleLeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.setPageIndex(0)}
                    aria-label='First Page'
                  />
                </Tooltip>

                <Tooltip title='Previous Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<LeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.previousPage()}
                    aria-label='Previous Page'
                  />
                </Tooltip>

                <span>
                  Page {table.getState().pagination.pageIndex + 1} of{' '}
                  {table.getPageCount() || 1}
                </span>

                <Tooltip title='Next Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<RightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.nextPage()}
                    aria-label='Next Page'
                  />
                </Tooltip>

                <Tooltip title='Last Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleRightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    aria-label='Last Page'
                  />
                </Tooltip>
              </Styled.PaginationControls>
            </Styled.PaginationContainer>
          </>
        )}
      </Styled.TableContainer>
    </ThemeProvider>
  );
};

export default BillerTanStackTable;
